import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate, useLocation } from "react-router-dom";

//Components
import Layout from "../../../Layout/Layout";
import { InputField } from "../../../Components/formElements/InputField";
import { TextAreaField } from "../../../Components/formElements/TextAreaField";
import { ComboboxField } from "../../../Components/formElements/ComboboxField";
import { validateRequired } from "../../../common/Validations";
import ItemService from "../../../services/supplier_appCommonService";
import SupplierConstants from "../../../common/SupplierConstants";
import {
  departmentOptions,
  financialYearOptions,
  budgetPeriodOptions,
  currencyOptions,
  statusOptions,
  categoryFallbackOptions,
  getBudgetFormTemplate,
} from "./budgetConstants";
import { getBusinessUnitOptions, getCostCenterOptions } from "../Sourcing/PR/PRConstant";

const BudgetCreationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  localStorage.setItem("menu", "Admin");
  localStorage.setItem("submenu", "Budget");

  const budgetId = location?.state?.budgetId || new URLSearchParams(location.search).get("id");
  const isUpdateMode = !!budgetId;

  const [isCardVisible, setCardVisible] = useState(true);
  const toggleCardVisibility = () => {
    setCardVisible(!isCardVisible);
  };

  const [formInputs, setFormInputs] = useState(getBudgetFormTemplate());
  const [companyOptions, setCompanyOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(categoryFallbackOptions);
  const [disabled, setDisabled] = useState(false);
  const [getIsLoading, setIsLoading] = useState(false);

  /* ---------------------------- Load dropdown master data --------------------------------*/
  useEffect(() => {
    loadCompanyOptions();
    loadCategoryOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanyOptions = async () => {
    try {
      await ItemService.getCompanyList({ page: 1, per_page: 500 }).then((item) => {
        if (item?.status === 200 && item?.data?.length) {
          setCompanyOptions(
            item.data.map((c, i) => ({
              id: c._id || i + 1,
              name: c.companyName || "",
              code: c.companyCode || "",
              description: c.companyName || "",
            }))
          );
        }
      });
    } catch (error) {
      console.log("loadCompanyOptions Failed:", error);
    }
  };

  const loadCategoryOptions = async () => {
    try {
      await ItemService.getMasterData({ codeType: "PurchaseCategory" }).then((item) => {
        if (item?.status === 200 && item?.data?.length) {
          setCategoryOptions(
            item.data.map((c, i) => ({
              id: c.id || c._id || i + 1,
              name: c.name || "",
              code: c.code || "",
              description: c.description || "",
            }))
          );
        }
      });
    } catch (error) {
      console.log("loadCategoryOptions Failed:", error);
      setCategoryOptions(categoryFallbackOptions);
    }
  };

  /* ---------------------------- Load Budget Detail in update mode --------------------------------*/
  useEffect(() => {
    if (budgetId) {
      getBudgetDetailData(budgetId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetId]);

  const getBudgetDetailData = async (id) => {
    setIsLoading(true);
    try {
      await ItemService.getBudgetDetail({ _id: id }).then((item) => {
        if (item?.status === 200 && item?.data) {
          let detail = item.data;
          setFormInputs({
            ...getBudgetFormTemplate(),
            ...detail,
            _id: detail._id,
            budgetAmount: detail.budgetAmount !== undefined ? detail.budgetAmount : "",
          });
        } else {
          toast.error(item?.message || "!! Unable to Get Budget Detail");
          navigate(SupplierConstants.BUDGET_LIST);
        }
      });
    } catch (error) {
      console.log("getBudgetDetailData Failed:", error);
      toast.error("!! Unable to Get Budget Detail");
    }
    setIsLoading(false);
  };

  /* ---------------------------- Form Field Change --------------------------------*/
  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Combobox value change - single stable handler keyed on the field's name prop
  // Guard: ComboboxField fires getvalue on mount too (no .name then) - never wipe loaded values
  const handleDropdownGetValue = useCallback((obj) => {
    if (!obj?.textField || !obj?.name) return;
    setFormInputs((prev) => ({ ...prev, [obj.textField]: obj.name }));
  }, []);

  /* ---------------------------- Validation + Submit --------------------------------*/
  const validateForm = () => {
    const errors = [];
    if (!validateRequired(formInputs?.budgetName)) errors.push("Budget Name");
    if (!validateRequired(formInputs?.company)) errors.push("Company");
    if (!validateRequired(formInputs?.businessUnit)) errors.push("Business Unit");
    if (!validateRequired(formInputs?.category)) errors.push("Category");
    if (!validateRequired(formInputs?.department)) errors.push("Department");
    if (!validateRequired(formInputs?.costCenter)) errors.push("Cost Center");
    if (!validateRequired(formInputs?.financialYear)) errors.push("Financial Year");
    if (!validateRequired(formInputs?.budgetPeriod)) errors.push("Budget Period");
    if (formInputs?.budgetAmount === "" || formInputs?.budgetAmount === null || formInputs?.budgetAmount === undefined)
      errors.push("Budget Amount");
    if (!validateRequired(formInputs?.currency)) errors.push("Currency");
    if (!validateRequired(formInputs?.startDate)) errors.push("Start Date");
    if (!validateRequired(formInputs?.endDate)) errors.push("End Date");
    if (!validateRequired(formInputs?.status)) errors.push("Status");
    return errors;
  };

  const handleCreateUpdateBudget = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast.error("Please fill required fields: " + errors.join(", "), {
        duration: 5000,
        position: "top-right",
      });
      return;
    }

    const toastId = toast.loading("Loading...", { position: "top-right" });
    setDisabled(true);

    let payload = { data: { ...formInputs, budgetAmount: Number(formInputs.budgetAmount) } };

    if (isUpdateMode) {
      payload.data._id = budgetId;
      await ItemService.updateBudget(payload).then((item) => {
        if (item?.status === 200) {
          toast.success(item?.message || "Budget updated successfully", { position: "top-right" });
          navigate(SupplierConstants.BUDGET_DETAILS, { state: { budgetId: budgetId } });
        } else {
          toast.error(item?.message || "Please try again !!", { duration: 4000, position: "top-right" });
        }
      });
    } else {
      await ItemService.addBudget(payload).then((item) => {
        if (item?.status === 200) {
          toast.success(item?.message || "Budget created successfully", { position: "top-right" });
          navigate(SupplierConstants.BUDGET_LIST);
        } else {
          toast.error(item?.message || "Please try again !!", { duration: 4000, position: "top-right" });
        }
      });
    }

    toast.dismiss(toastId);
    setDisabled(false);
  };

  const handleBack = () => {
    navigate(SupplierConstants.BUDGET_LIST);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={8} md={9} lg={9} className="mb-2">
                <h2 className="mainTitle">
                  {isUpdateMode ? "Update Budget" : "Create Budget"}
                </h2>
                <p className="subText">Budget Creation Details</p>
              </Col>
              <Col
                xs={4}
                md={3}
                lg={3}
                className="mb-2 d-flex justify-content-end align-items-center gap-2"
              >
                <button className="btnTable" onClick={handleBack}>
                  <i className="bi bi-arrow-left me-1"></i> Back
                </button>
                <button
                  className="btnTable"
                  disabled={disabled || getIsLoading}
                  onClick={handleCreateUpdateBudget}
                >
                  {isUpdateMode ? "Update Budget" : "Create Budget"}
                </button>
              </Col>
              <hr />
            </Row>

            <div className="accordion mb-4" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">BD</span>
                    </Col>
                    <Col xs={12} md={8} lg={8}>
                      <span className="headerTitle">Budget Information</span>
                      <span className="subHeaderTitle">
                        {isUpdateMode ? "Update Budget Details" : "Enter Budget Details"}
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    {getIsLoading ? (
                      <div className="text-center p-5">Loading...</div>
                    ) : (
                      <Row className="pt-2 pb-4">
                        {/* 1 - Budget Name */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            className="inputBox"
                            label="Budget Name"
                            name="budgetName"
                            id="budgetName"
                            placeholder="Annual IT Budget"
                            value={formInputs.budgetName || ""}
                            onChange={onInputChange}
                            required={true}
                          />
                        </Col>

                        {/* 2 - Company */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Company"
                            name="company"
                            id="company"
                            placeholder="Select Company"
                            data={companyOptions}
                            setValue={formInputs.company || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 3 - Business Unit */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Business Unit"
                            name="businessUnit"
                            id="businessUnit"
                            placeholder="Select Business Unit"
                            data={getBusinessUnitOptions}
                            setValue={formInputs.businessUnit || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 4 - Category */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Category"
                            name="category"
                            id="category"
                            placeholder="Select Category"
                            data={categoryOptions}
                            setValue={formInputs.category || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 5 - Department */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Department"
                            name="department"
                            id="department"
                            placeholder="Select Department"
                            data={departmentOptions}
                            setValue={formInputs.department || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 6 - Cost Center */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Cost Center"
                            name="costCenter"
                            id="costCenter"
                            placeholder="Select Cost Center"
                            data={getCostCenterOptions}
                            setValue={formInputs.costCenter || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 7 - Financial Year */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Financial Year"
                            name="financialYear"
                            id="financialYear"
                            placeholder="Select Financial Year"
                            data={financialYearOptions}
                            setValue={formInputs.financialYear || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 8 - Budget Period */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Budget Period"
                            name="budgetPeriod"
                            id="budgetPeriod"
                            placeholder="Select Budget Period"
                            data={budgetPeriodOptions}
                            setValue={formInputs.budgetPeriod || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 9 - Budget Amount */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            className="inputBox"
                            label="Budget Amount"
                            type="number"
                            name="budgetAmount"
                            id="budgetAmount"
                            placeholder="1000000"
                            value={formInputs.budgetAmount || ""}
                            onChange={onInputChange}
                            required={true}
                          />
                        </Col>

                        {/* 10 - Currency */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Currency"
                            name="currency"
                            id="currency"
                            placeholder="Select Currency"
                            data={currencyOptions}
                            setValue={formInputs.currency || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>

                        {/* 11 - Start Date */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            className="inputBox"
                            label="Start Date"
                            type="date"
                            name="startDate"
                            id="startDate"
                            value={formInputs.startDate || ""}
                            onChange={onInputChange}
                            min="2020-01-01"
                            required={true}
                          />
                        </Col>

                        {/* 12 - End Date */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            className="inputBox"
                            label="End Date"
                            type="date"
                            name="endDate"
                            id="endDate"
                            value={formInputs.endDate || ""}
                            onChange={onInputChange}
                            min="2020-01-01"
                            required={true}
                          />
                        </Col>

                        {/* 13 - Description */}
                        <Col xs={12} md={8} lg={8} className="commTopButtonRightLeftPadding">
                          <TextAreaField
                            className="inputBox"
                            label="Description"
                            type="text"
                            name="description"
                            id="description"
                            placeholder="Annual department budget"
                            value={formInputs.description || ""}
                            onChange={onInputChange}
                          />
                        </Col>

                        {/* 14 - Status */}
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <ComboboxField
                            label="Status"
                            name="status"
                            id="status"
                            placeholder="Select Status"
                            data={statusOptions}
                            setValue={formInputs.status || ""}
                            getvalue={handleDropdownGetValue}
                            required={true}
                          />
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default BudgetCreationPage;
